<div class="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-6">

    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 shadow-sm p-5 rounded-2xl">
        <div>
            <h1 class="text-xl font-bold tracking-tight text-slate-900">{{ 'obligations.title' | translate }}</h1>
            <p class="text-xs text-slate-500 max-w-2xl mt-1">{{ 'obligations.subtitle' | translate }}</p>
        </div>
        <div class="flex items-center gap-3">
            <!-- Site Selector (narrows across all tabs) -->
            @if (sites().length > 1) {
            <div class="min-w-[200px]">
                <label class="sr-only" for="ob-site">{{ 'obligations.site' | translate }}</label>
                <select id="ob-site"
                        class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                        [ngModel]="siteFilter()" (ngModelChange)="onSiteChange($event)">
                    <option value="">{{ 'obligations.allSites' | translate }}</option>
                    @for (site of sites(); track site.id) {
                    <option [value]="site.id">{{ site.name }}</option>
                    }
                </select>
            </div>
            }

            <!-- Download CSV for Obligations Tab -->
            @if (activeTab() === 'obligations') {
            <jjames-button variant="outline" matIconLeft="download"
                           [loading]="isExporting()" [disabled]="isExporting()"
                           (clicked)="exportCsv()">
                {{ (isExporting() ? 'obligations.exporting' : 'obligations.exportCsv') | translate }}
            </jjames-button>
            }
        </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="flex border-b border-slate-200 gap-6 text-sm font-medium text-slate-600 overflow-x-auto">
        <!-- 1. Dashboard (Overview) -->
        <button type="button" class="pb-3 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap"
                [class.border-blue-600]="activeTab() === 'overview'"
                [class.text-blue-600]="activeTab() === 'overview'"
                [class.border-transparent]="activeTab() !== 'overview'"
                (click)="setTab('overview')">
            <mat-icon class="!w-4 !h-4 !text-[18px]">dashboard</mat-icon>
            {{ 'obligations.tabOverview' | translate }}
        </button>

        <!-- 2. Revenue -->
        <button type="button" class="pb-3 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap"
                [class.border-blue-600]="activeTab() === 'revenue'"
                [class.text-blue-600]="activeTab() === 'revenue'"
                [class.border-transparent]="activeTab() !== 'revenue'"
                (click)="setTab('revenue')">
            <mat-icon class="!w-4 !h-4 !text-[18px]">account_balance</mat-icon>
            {{ 'obligations.tabRevenue' | translate }}
        </button>

        <!-- 3. Obligations -->
        <button type="button" class="pb-3 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap"
                [class.border-blue-600]="activeTab() === 'obligations'"
                [class.text-blue-600]="activeTab() === 'obligations'"
                [class.border-transparent]="activeTab() !== 'obligations'"
                (click)="setTab('obligations')">
            <mat-icon class="!w-4 !h-4 !text-[18px]">receipt_long</mat-icon>
            {{ 'obligations.tabObligations' | translate }} ({{ totalCount() }})
        </button>

        <!-- 4. All Payments (Unified) -->
        <button type="button" class="pb-3 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap"
                [class.border-blue-600]="activeTab() === 'all-payments'"
                [class.text-blue-600]="activeTab() === 'all-payments'"
                [class.border-transparent]="activeTab() !== 'all-payments'"
                (click)="setTab('all-payments')">
            <mat-icon class="!w-4 !h-4 !text-[18px]">payments</mat-icon>
            {{ 'obligations.tabAllPayments' | translate }}
        </button>

        <!-- 5. Reports & Analytics -->
        <button type="button" class="pb-3 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap"
                [class.border-blue-600]="activeTab() === 'reports'"
                [class.text-blue-600]="activeTab() === 'reports'"
                [class.border-transparent]="activeTab() !== 'reports'"
                (click)="setTab('reports')">
            <mat-icon class="!w-4 !h-4 !text-[18px]">bar_chart</mat-icon>
            {{ 'obligations.tabReports' | translate }}
        </button>

        <!-- 6. Coin Machines -->
        <button type="button" class="pb-3 border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap"
                [class.border-blue-600]="activeTab() === 'coin-machines'"
                [class.text-blue-600]="activeTab() === 'coin-machines'"
                [class.border-transparent]="activeTab() !== 'coin-machines'"
                (click)="setTab('coin-machines')">
            <!-- toll fills its whole glyph box, so the usual 16px box clips it -->
            <mat-icon class="!w-[18px] !h-[18px] !text-[18px]">toll</mat-icon>
            {{ 'obligations.tabCoinMachines' | translate }}
        </button>
    </div>

    <!-- TAB 1: EXECUTIVE DASHBOARD OVERVIEW -->
    @if (activeTab() === 'overview') {
    <div class="space-y-6">
        @if (statsFailed()) {
        <div class="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
            <mat-icon class="!w-5 !h-5 !text-[20px] shrink-0">error_outline</mat-icon>
            <span>{{ 'obligations.statsUnavailable' | translate }}</span>
        </div>
        }
        @if (hasMixedCurrencies()) {
        <div class="flex items-start gap-2 bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-4 py-3 text-sm">
            <mat-icon class="!w-5 !h-5 !text-[20px] shrink-0">info_outline</mat-icon>
            <span>{{ 'obligations.mixedCurrencies' | translate: { currency: currencyCode() } }}</span>
        </div>
        }

        <!-- Unified Top KPI Summary Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Total Net Collected Revenue -->
            <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                    <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">Total Net Revenue</p>
                    <h3 class="text-2xl font-bold text-slate-900 mt-1">
                        {{ statsLoaded() ? (totalEstateRevenue() | currency: currencyCode() : 'symbol-narrow' : '1.2-2') : '—' }}
                    </h3>
                    <span class="inline-flex items-center text-xs text-emerald-600 font-medium mt-1">
                        <mat-icon class="!w-3 !h-3 !text-[12px]">account_balance</mat-icon>
                        Direct + sweep collected
                    </span>
                </div>
                <div class="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                    <mat-icon>payments</mat-icon>
                </div>
            </div>

            <!-- Total Outstanding Unpaid Bills -->
            <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                    <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'obligations.kpiOpenAmount' | translate }}</p>
                    <h3 class="text-2xl font-bold text-slate-900 mt-1">
                        {{ statsLoaded() ? (outstandingAmount() | currency: currencyCode() : 'symbol-narrow' : '1.2-2') : '—' }}
                    </h3>
                    @if (expiredAmount() > 0) {
                    <span class="inline-flex items-center text-xs text-rose-600 font-medium mt-1">
                        <mat-icon class="!w-3 !h-3 !text-[12px]">schedule</mat-icon>
                        {{ 'obligations.kpiExpiredHint' | translate: {
                            amount: (expiredAmount() | currency: currencyCode() : 'symbol-narrow' : '1.2-2')
                        } }}
                    </span>
                    } @else {
                    <span class="inline-flex items-center text-xs text-blue-600 font-medium mt-1">
                        <mat-icon class="!w-3 !h-3 !text-[12px]">hourglass_empty</mat-icon>
                        {{ 'obligations.kpiOpenAmountHint' | translate }}
                    </span>
                    }
                </div>
                <div class="p-3 bg-blue-50 text-blue-600 rounded-xl">
                    <mat-icon>pending_actions</mat-icon>
                </div>
            </div>

            <!-- Total Cancelled / Forgiven -->
            <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                    <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'obligations.kpiWaivedAmount' | translate }}</p>
                    <h3 class="text-2xl font-bold text-slate-900 mt-1">
                        {{ statsLoaded() ? (waivedCancelledAmount() | currency: currencyCode() : 'symbol-narrow' : '1.2-2') : '—' }}
                    </h3>
                    <span class="inline-flex items-center text-xs text-amber-600 font-medium mt-1">
                        <mat-icon class="!w-3 !h-3 !text-[12px]">money_off</mat-icon>
                        {{ 'obligations.kpiWaivedAmountHint' | translate }}
                    </span>
                </div>
                <div class="p-3 bg-amber-50 text-amber-600 rounded-xl">
                    <mat-icon>block</mat-icon>
                </div>
            </div>

            <!-- Paid Percentage / Success Rate -->
            <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                    <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'obligations.kpiSuccessRate' | translate }}</p>
                    <h3 class="text-2xl font-bold text-slate-900 mt-1">{{ collectionRateLabel() }}</h3>
                    <span class="inline-flex items-center text-xs text-slate-500 font-medium mt-1">
                        <mat-icon class="!w-3 !h-3 !text-[12px]">check_circle</mat-icon>
                        {{ 'obligations.kpiSuccessRateHint' | translate }}
                    </span>
                </div>
                <div class="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                    <mat-icon>pie_chart</mat-icon>
                </div>
            </div>
        </div>

        <!-- Estate Revenue Streams Split Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Direct Revenue Stream -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
                <div>
                    <div class="flex items-center justify-between">
                        <span class="text-xs font-bold uppercase tracking-wider text-slate-400">Direct Payment Takings</span>
                        <span class="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-md">Live Rails</span>
                    </div>
                    <h4 class="text-3xl font-extrabold text-slate-900 mt-2">
                        {{ directNetRevenue() | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}
                    </h4>
                    <p class="text-xs text-slate-500 mt-1">
                        Real-time payments at columns, payment terminals, and mobile web app.
                    </p>
                </div>
                <div class="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <button type="button" class="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            (click)="setTab('revenue')">
                        View Direct Revenue <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">arrow_forward</mat-icon>
                    </button>
                </div>
            </div>

            <!-- Late-Payment Sweep Receivables Stream -->
            <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
                <div>
                    <div class="flex items-center justify-between">
                        <span class="text-xs font-bold uppercase tracking-wider text-slate-400">Late Payment Sweep Receivables</span>
                        <span class="px-2 py-0.5 bg-amber-50 text-amber-800 text-xs font-semibold rounded-md">Receivables</span>
                    </div>
                    <h4 class="text-3xl font-extrabold text-slate-900 mt-2">
                        {{ (totalCollectedAmount() + outstandingAmount()) | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}
                    </h4>
                    <p class="text-xs text-slate-500 mt-1">
                        {{ totalCount() }} unpaid bills generated by late-payment sweep for unpaid stays.
                    </p>
                </div>
                <div class="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <button type="button" class="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            (click)="setTab('obligations')">
                        Manage Obligations <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">arrow_forward</mat-icon>
                    </button>
                </div>
            </div>
        </div>

        <!-- Obligations Status Breakdown Cards -->
        <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-4">
                <div>
                    <h2 class="text-base font-semibold text-slate-900">{{ 'obligations.chartTitle' | translate }}</h2>
                    <p class="text-xs text-slate-500 mt-0.5">{{ 'obligations.kpiScopeAll' | translate }}</p>
                </div>
                <button type="button" class="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        (click)="setTab('reports')">
                    Open Full Reports & Charts <mat-icon class="!w-3.5 !h-3.5 !text-[14px]">arrow_forward</mat-icon>
                </button>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                @for (st of statuses; track st) {
                <div class="p-3.5 rounded-xl border border-slate-100 bg-slate-50 flex flex-col justify-between">
                    <span class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {{ 'obligations.status_' + st | translate }}
                    </span>
                    <span class="text-xl font-bold text-slate-900 mt-2">
                        {{ statusCounts()[st] || 0 }}
                    </span>
                </div>
                }
            </div>
        </div>
    </div>
    }

    <!-- TAB 2: REVENUE — Direct Payments & Rails -->
    @if (activeTab() === 'revenue') {
    <app-payments-list [parkingSpaceId]="siteFilter()"></app-payments-list>
    }

    <!-- TAB 3: OBLIGATIONS — Late Payment Sweep Debts & Operator Actions -->
    @if (activeTab() === 'obligations') {
    <!-- Filters -->
    <section class="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
        <div class="flex flex-wrap items-end gap-3">
            <div class="flex-1 min-w-[180px]">
                <label class="kv-label block mb-1" for="ob-status">{{ 'obligations.status' | translate }}</label>
                <select id="ob-status"
                        class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                        [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
                    <option value="">{{ 'obligations.allStatuses' | translate }}</option>
                    @for (status of statuses; track status) {
                    <option [value]="status">{{ 'obligations.status_' + status | translate }}</option>
                    }
                </select>
            </div>

            <div class="flex-1 min-w-[260px]">
                <label class="kv-label block mb-1" for="ob-plate">{{ 'obligations.plate' | translate }}</label>
                <input id="ob-plate" type="text"
                       class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                       [placeholder]="'obligations.platePlaceholder' | translate"
                       [ngModel]="plateFilter()" (ngModelChange)="plateFilter.set($event)"
                       (keyup.enter)="applyFilters()" />
            </div>

            <div class="flex gap-2">
                <jjames-button variant="primary" matIconLeft="search" (clicked)="applyFilters()">
                    {{ 'obligations.apply' | translate }}
                </jjames-button>
                <jjames-button variant="ghost" (clicked)="clearFilters()">
                    {{ 'obligations.clear' | translate }}
                </jjames-button>
            </div>
        </div>
    </section>

    <!-- Obligations Table -->
    <section class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        @if (isLoading()) {
        <div class="flex items-center justify-center py-16">
            <mat-spinner diameter="32"></mat-spinner>
        </div>
        } @else if (obligations().length === 0) {
        <div class="flex flex-col items-center justify-center py-16 text-center px-6">
            <mat-icon class="!w-10 !h-10 !text-[40px] leading-10 text-slate-300">receipt_long</mat-icon>
            <p class="text-sm font-medium text-slate-700 mt-3">{{ 'obligations.emptyTitle' | translate }}</p>
            <p class="text-sm text-slate-500 mt-1 max-w-md">{{ 'obligations.emptyBody' | translate }}</p>
        </div>
        } @else {
        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead>
                    <tr class="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50/50">
                        <th class="w-10 px-3 py-3 text-center"></th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'obligations.colCreated' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'obligations.colPlate' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'obligations.colSpace' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'obligations.colStay' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'obligations.colAmount' | translate }}</th>
                        <th class="px-4 py-3 font-medium whitespace-nowrap">{{ 'obligations.colStatus' | translate }}</th>
                        <th class="px-4 py-3 font-medium text-right whitespace-nowrap min-w-[220px]">{{ 'obligations.colActions' | translate }}</th>
                    </tr>
                </thead>
                <tbody>
                    @for (obligation of obligations(); track obligation.id) {
                    <!-- Main Summary Row -->
                    <tr class="border-b border-slate-100 hover:bg-slate-50/80 transition-colors cursor-pointer select-none"
                        [class.bg-blue-50_30]="isRowExpanded(obligation.id)"
                        (click)="toggleRowExpand(obligation.id)">

                        <!-- Expand Chevron Toggle -->
                        <td class="px-3 py-3 text-center text-slate-400">
                            <button type="button"
                                    class="expand-btn p-1 rounded-md hover:bg-slate-200/70 transition-colors focus:outline-none"
                                    [class.is-expanded]="isRowExpanded(obligation.id)"
                                    (click)="$event.stopPropagation(); toggleRowExpand(obligation.id)"
                                    [attr.aria-label]="'Toggle details'">
                                <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 transition-transform duration-200 block">
                                    {{ isRowExpanded(obligation.id) ? 'expand_more' : 'chevron_right' }}
                                </mat-icon>
                            </button>
                        </td>

                        <!-- Date Created -->
                        <td class="px-4 py-3 whitespace-nowrap text-slate-600">
                            {{ obligation.createdAt | date:'medium' }}
                        </td>

                        <!-- License Plate -->
                        <td class="px-4 py-3 whitespace-nowrap font-medium text-slate-900">
                            <span class="inline-block px-2 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono text-xs font-bold text-slate-800">
                                {{ obligation.licensePlate || '—' }}
                            </span>
                        </td>

                        <!-- Space Name -->
                        <td class="px-4 py-3 whitespace-nowrap text-slate-600">
                            {{ obligation.parkingSpaceName || '—' }}
                        </td>

                        <!-- Stay Length -->
                        <td class="px-4 py-3 whitespace-nowrap text-slate-600">
                            @if (duration(obligation); as stayLength) {
                            {{ stayLength }}
                            } @else if (obligation.startedAt) {
                            <span class="attr-pill attr-pill--blue">{{ 'obligations.stayOpen' | translate }}</span>
                            } @else {
                            —
                            }
                        </td>

                        <!-- Amount -->
                        <td class="px-4 py-3 whitespace-nowrap font-semibold text-slate-900">
                            {{ obligation.amount | number:'1.2-2' }} {{ obligation.currency || 'EUR' }}
                            @if ((obligation.netPaidAmount ?? 0) > 0 && obligation.status !== 'Paid') {
                            <div class="text-xs font-normal text-emerald-700 mt-0.5">
                                {{ 'obligations.partiallyPaid' | translate: {
                                    amount: (obligation.netPaidAmount | number:'1.2-2')
                                } }}
                            </div>
                            }
                        </td>

                        <!-- Status -->
                        <td class="px-4 py-3 whitespace-nowrap">
                            <span class="attr-pill"
                                  [class.attr-pill--green]="obligation.status === 'Paid'"
                                  [class.attr-pill--blue]="obligation.status === 'Open'"
                                  [class.attr-pill--amber]="obligation.status === 'HeldForReview'">
                                {{ 'obligations.status_' + obligation.status | translate }}
                            </span>
                        </td>

                        <!-- Action Buttons -->
                        <td class="px-4 py-3 whitespace-nowrap text-right" (click)="$event.stopPropagation()">
                            @if (isLive(obligation)) {
                            <div class="inline-flex items-center gap-1.5 justify-end">
                                <jjames-button variant="outline" size="xs" (clicked)="act(obligation, 'reissue')">
                                    {{ 'obligations.reissue' | translate }}
                                </jjames-button>
                                <jjames-button variant="outline" size="xs" (clicked)="act(obligation, 'cancel')">
                                    {{ 'obligations.cancel' | translate }}
                                </jjames-button>
                                <jjames-button variant="warning" size="xs" (clicked)="act(obligation, 'waive')">
                                    {{ 'obligations.waive' | translate }}
                                </jjames-button>
                            </div>
                            } @else {
                            <span class="text-xs text-slate-400 font-medium">{{ 'obligations.terminal' | translate }}</span>
                            }
                        </td>
                    </tr>

                    <!-- Expandable Detail Drawer Row -->
                    @if (isRowExpanded(obligation.id)) {
                    <tr class="bg-slate-50/90 border-b border-slate-200">
                        <td colspan="8" class="p-4 sm:p-5">
                            <div class="expand-drawer-content">
                                <div class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-200/80 mb-4">
                                    <div class="flex items-center gap-2">
                                        <span class="kv-label">Parking Process ID:</span>
                                        <span class="font-mono text-xs text-slate-800 font-semibold">{{ obligation.parkingProcessId || '—' }}</span>
                                        @if (obligation.parkingProcessId) {
                                        <button type="button" class="copy-icon-btn" (click)="copyToClipboard(obligation.parkingProcessId, 'Parking Process ID')" title="Copy Process ID">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-slate-400 hover:text-blue-600">content_copy</mat-icon>
                                        </button>
                                        }
                                    </div>

                                    <div class="flex items-center gap-2">
                                        <span class="attr-pill"
                                              [class.attr-pill--green]="obligation.status === 'Paid'"
                                              [class.attr-pill--blue]="obligation.status === 'Open'"
                                              [class.attr-pill--amber]="obligation.status === 'HeldForReview'">
                                            {{ 'obligations.status_' + obligation.status | translate }}
                                        </span>
                                    </div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <!-- 1. Parking Stay Timing -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
                                        <div class="flex items-center gap-2 mb-2.5 text-slate-700 font-semibold text-xs uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-blue-600">access_time</mat-icon>
                                            Parking Session
                                        </div>
                                        <div class="space-y-1.5 text-xs">
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Entry:</span>
                                                <span class="font-medium text-slate-800">{{ obligation.startedAt ? (obligation.startedAt | date:'medium') : '—' }}</span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Exit:</span>
                                                <span class="font-medium text-slate-800">{{ obligation.endedAt ? (obligation.endedAt | date:'medium') : 'Still parked' }}</span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Duration:</span>
                                                <span class="font-semibold text-blue-700">{{ duration(obligation) || 'In progress' }}</span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Space:</span>
                                                <span class="font-medium text-slate-800">{{ obligation.parkingSpaceName || '—' }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 2. Financial & Payment Info -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
                                        <div class="flex items-center gap-2 mb-2.5 text-slate-700 font-semibold text-xs uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-emerald-600">payments</mat-icon>
                                            Financial Breakdown
                                        </div>
                                        <div class="space-y-1.5 text-xs">
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Total Amount:</span>
                                                <span class="font-bold text-slate-900">{{ obligation.amount | number:'1.2-2' }} {{ obligation.currency || 'EUR' }}</span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Net Paid:</span>
                                                <span class="font-medium" [class.text-emerald-700]="(obligation.netPaidAmount ?? 0) > 0">
                                                    {{ (obligation.netPaidAmount ?? 0) | number:'1.2-2' }} {{ obligation.currency || 'EUR' }}
                                                </span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Provider / Method:</span>
                                                <span class="font-medium text-slate-800">{{ obligation.paymentProviderKind || '—' }}</span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-slate-500">Paid At:</span>
                                                <span class="font-medium text-slate-800">{{ obligation.paidAt ? (obligation.paidAt | date:'medium') : '—' }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 3. Note & Reason Full Content -->
                                    <div class="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs flex flex-col">
                                        <div class="flex items-center gap-2 mb-2 text-slate-700 font-semibold text-xs uppercase tracking-wide">
                                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-amber-600">notes</mat-icon>
                                            Note & Reason
                                        </div>
                                        <div class="flex-1 bg-slate-50 rounded-lg p-2.5 text-xs text-slate-700 leading-relaxed border border-slate-100 overflow-y-auto max-h-[100px]">
                                            {{ obligation.reason || 'No note or explanation was recorded for this item.' }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Action Buttons in Drawer -->
                                @if (isLive(obligation)) {
                                <div class="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-200/80 bg-white/70 p-3 rounded-xl border border-slate-200">
                                    <div class="text-xs text-slate-500">
                                        <span class="font-semibold text-slate-700">Live Actions:</span> Modify or forgive this bill before collection.
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <jjames-button variant="outline" size="sm" (clicked)="act(obligation, 'reissue')">
                                            {{ 'obligations.reissue' | translate }}
                                        </jjames-button>
                                        <jjames-button variant="outline" size="sm" (clicked)="act(obligation, 'cancel')">
                                            {{ 'obligations.cancel' | translate }}
                                        </jjames-button>
                                        <jjames-button variant="warning" size="sm" (clicked)="act(obligation, 'waive')">
                                            {{ 'obligations.waive' | translate }}
                                        </jjames-button>
                                    </div>
                                </div>
                                }
                            </div>
                        </td>
                    </tr>
                    }
                    }
                </tbody>
            </table>
        </div>

        <div class="border-t border-slate-100 px-4 py-3">
            <jjames-paginator [config]="pagination()" (pageChange)="onPageChange($event)"></jjames-paginator>
        </div>
        }
    </section>
    }

    <!-- TAB 4: ALL PAYMENTS — Unified Payment Attempts & Ledger -->
    @if (activeTab() === 'all-payments') {
    <app-all-payments-list [parkingSpaceId]="siteFilter()"></app-all-payments-list>
    }

    <!-- TAB 5: REPORTS & ANALYTICS — Simple and Powerful Interactive Charts -->
    @if (activeTab() === 'reports') {
    <app-reports-analytics [parkingSpaceId]="siteFilter()"></app-reports-analytics>
    }

    @if (activeTab() === 'coin-machines') {
    <app-coin-machines [parkingSpaceId]="siteFilter()" [sites]="sites()"></app-coin-machines>
    }
</div>
